import {
  DomainTuple,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
} from 'victory';
import {
  BandConvergenceDivergence,
  IndicatorSingleValue,
} from '../../../screens/ProfitCalculations/types';
import { useMemo } from 'react';
import { XAxisInfo } from '../../MultiChart';
import { colors } from '../../../colors';
import { lightenColor } from '../../../helpers/colors';
import IndicatorLabel from '../IndicatorLabel';

interface Props {
  bandcdInfo: BandConvergenceDivergence;
  showWeightedHistogram: boolean;
  label: string;

  width?: number | undefined;
  height?: number | undefined;
  xAxisInfo?: XAxisInfo | undefined;
  showXAxis?: boolean;
}

type DataPoint = {
  x: number;
  y: number;
  color?: string;
};

const transformToDataPoints = (
  data: IndicatorSingleValue[] | undefined,
): DataPoint[] => {
  if (!data?.length) {
    return [];
  }

  /** Because the candles start at the beginning of the candle
   * and the indicator values is al the end of the candle
   * We have to change position a little
   */
  const range =
    data[data.length - 1].timestamp - data[data.length - 2].timestamp;
  return data.map((d) => ({ x: d.timestamp - range, y: d.value })) ?? [];
};

const BandCDChart = (props: Props) => {
  const {
    bandcdInfo,
    label,
    showWeightedHistogram,
    width,
    height,
    xAxisInfo,
    showXAxis,
  } = props;

  const bandCDDataPoints = useMemo(
    () =>
      transformToDataPoints(bandcdInfo.divergenceLine).filter(
        (d) => d.x >= (xAxisInfo?.domain.x[0] ?? 0),
      ),
    [bandcdInfo.divergenceLine, xAxisInfo?.domain.x],
  );

  const signalDataPoints = useMemo(
    () =>
      transformToDataPoints(bandcdInfo.signalLine).filter(
        (d) => d.x >= (xAxisInfo?.domain.x[0] ?? 0),
      ),
    [bandcdInfo.signalLine, xAxisInfo?.domain.x],
  );

  const histogramDataPoints = useMemo(() => {
    const dataPoints = transformToDataPoints(
      showWeightedHistogram
        ? bandcdInfo.weightedHistogram
        : bandcdInfo.histogram,
    ).filter((d) => d.x >= (xAxisInfo?.domain.x[0] ?? 0));

    for (let i = 0; i < dataPoints.length; i++) {
      const dataPoint = dataPoints[i];

      if (i === 0) {
        dataPoint.color = dataPoint.y >= 0 ? colors.green : colors.red;
        continue;
      }

      const previousDataPoints = dataPoints[i - 1];
      const change = dataPoint.y - previousDataPoints.y;

      if (change > 0 && dataPoint.y > 0) {
        dataPoint.color = colors.green;
      } else if (change > 0 && dataPoint.y < 0) {
        dataPoint.color = colors.lightRed;
      } else if (change < 0 && dataPoint.y > 0) {
        dataPoint.color = lightenColor(colors.green);
      } else if (change < 0 && dataPoint.y < 0) {
        dataPoint.color = colors.red;
      }
    }

    return dataPoints;
  }, [
    bandcdInfo.histogram,
    bandcdInfo.weightedHistogram,
    showWeightedHistogram,
    xAxisInfo?.domain.x,
  ]);

  const yAxisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      tickLabels: { fontSize: 10, padding: 5, fill: '#999' },
    }),
    [],
  );

  const domain = useMemo(() => {
    if (!xAxisInfo?.domain.x) {
      return undefined;
    }

    const x = xAxisInfo.domain.x;

    const filteredBandCDDataPoints = bandCDDataPoints.filter(
      (b) => b.x >= x[0],
    );
    const filteredHistogramDataPoints = histogramDataPoints.filter(
      (b) => b.x >= x[0],
    );

    const minY = Math.min(...filteredHistogramDataPoints.map((b) => b.y));

    const maxY = Math.max(
      ...filteredBandCDDataPoints.map((b) => b.y),
      ...filteredHistogramDataPoints.map((b) => b.y),
    );

    const grace = maxY * 0.1;

    const y = [minY - grace, maxY + grace] as DomainTuple;

    return { x, y };
  }, [bandCDDataPoints, histogramDataPoints, xAxisInfo?.domain.x]);

  const barWidth = useMemo(() => {
    if (!width || !domain) {
      return undefined;
    }

    const outerLeft = domain?.x[0];
    const outerRight = domain?.x[1];
    const smallestX = Math.min(...histogramDataPoints.map((h) => h.x));
    const biggestX = Math.max(...histogramDataPoints.map((h) => h.x));
    const amountOfBars = histogramDataPoints.length;

    const totalRange = outerRight - outerLeft;

    const barsRange =
      totalRange -
      (Math.max(smallestX, outerLeft) - Math.min(smallestX, outerLeft)) -
      (Math.max(biggestX, outerRight) - Math.min(biggestX, outerRight));

    const ratio = barsRange / totalRange;
    const totalBarsWidth = width * ratio;

    const totalBarChartWidth = totalBarsWidth / amountOfBars;

    return totalBarChartWidth * 0.7;
  }, [domain, histogramDataPoints, width]);

  const chartWidth = width;
  const chartHeight = height || 120;

  return (
    <div
      className="candlesticks-container"
      style={{
        width: chartWidth,
        height: chartHeight,
      }}
    >
      <IndicatorLabel label={label} />
      <VictoryChart
        width={chartWidth}
        domain={domain}
        domainPadding={xAxisInfo?.domainPadding}
        height={height}
        padding={{
          ...xAxisInfo?.chartPadding,
          top: 0,
          bottom: showXAxis ? 30 : 0,
        }}
      >
        <VictoryAxis
          tickFormat={showXAxis ? xAxisInfo?.tickFormat : () => ''}
          style={{ ...xAxisInfo?.axisStyle, ...(!showXAxis && { ticks: {} }) }}
          tickValues={xAxisInfo?.tickValues}
          tickLabelComponent={<VictoryLabel dy={chartHeight / 2.5} />}
        />
        <VictoryAxis
          dependentAxis
          style={yAxisStyle}
          orientation={'right'}
          tickLabelComponent={<VictoryLabel dx={5} />}
          tickCount={5}
          tickFormat={() => ''}
        />

        <VictoryBar
          data={histogramDataPoints}
          style={{
            data: {
              fill: (d) => {
                return d.datum.color;
              },
            },
          }}
          barWidth={barWidth}
        />

        <VictoryLine
          data={bandCDDataPoints}
          style={{
            data: { stroke: colors.blue },
          }}
        />
        <VictoryLine
          data={signalDataPoints}
          style={{
            data: { stroke: colors.orange },
          }}
        />
      </VictoryChart>
    </div>
  );
};

export default BandCDChart;
